import { createFileRoute } from "@tanstack/react-router";
import { EmptyState, PageHeader } from "@/components/page";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { timeAgo } from "@/lib/format";
import { useDeviceNames, useVault } from "@/lib/hooks";

export const Route = createFileRoute("/_app/inventory")({ component: InventoryPage });

interface RepoEntry {
  path: string;
  remote: string | null;
  branch: string | null;
}

function asRepos(payload: unknown): RepoEntry[] {
  const p = payload as { repos?: unknown } | null;
  if (!p || !Array.isArray(p.repos)) return [];
  return p.repos.map((r) => {
    const x = r as Partial<RepoEntry>;
    return { path: x.path ?? "", remote: x.remote ?? null, branch: x.branch ?? null };
  });
}

function InventoryPage() {
  const v = useVault();
  const deviceName = useDeviceNames();
  const inv = v.snapshot?.inventory ?? [];
  const repos = inv.filter((e) => e.kind === "git_repos");
  const brew = inv.filter((e) => e.kind === "brewfile");
  const plugins = inv.filter((e) => e.kind === "claude_plugins");
  const tools = inv.filter((e) => e.kind === "tool_versions");

  // Group repos by remote so you can see which devices have which checkout.
  const byRemote = new Map<
    string,
    { remote: string; entries: { device: string; path: string; branch: string | null }[] }
  >();
  for (const e of repos) {
    for (const r of asRepos(e.payload)) {
      const key = r.remote ?? r.path;
      const g = byRemote.get(key) ?? { remote: key, entries: [] };
      g.entries.push({ device: e.device_id, path: r.path, branch: r.branch });
      byRemote.set(key, g);
    }
  }
  const groups = [...byRemote.values()].sort((a, b) => a.remote.localeCompare(b.remote));

  return (
    <>
      <PageHeader
        title="Inventory"
        description="Metadata the daemon collects so a new Mac can be rebuilt: repos, Homebrew bundle, plugins, tool versions."
      />
      {inv.length === 0 ? (
        <EmptyState title="No inventory yet">The daemon refreshes inventory every few hours and on wake.</EmptyState>
      ) : (
        <Tabs defaultValue="repos">
          <TabsList>
            <TabsTrigger value="repos">Repos ({groups.length})</TabsTrigger>
            <TabsTrigger value="brew">Brewfile</TabsTrigger>
            <TabsTrigger value="plugins">Plugins</TabsTrigger>
            <TabsTrigger value="tools">Tools</TabsTrigger>
          </TabsList>
          <TabsContent value="repos">
            <ul className="divide-y rounded-xl border bg-card">
              {groups.map((g) => (
                <li key={g.remote} className="px-4 py-2.5">
                  <p className="truncate font-mono text-sm">{g.remote}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {g.entries.map((e, i) => (
                      <Badge key={i} variant="secondary" className="font-normal">
                        {deviceName(e.device)} · {e.path}
                        {e.branch ? ` @ ${e.branch}` : ""}
                      </Badge>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </TabsContent>
          {(
            [
              ["brew", brew],
              ["plugins", plugins],
              ["tools", tools],
            ] as const
          ).map(([key, entries]) => (
            <TabsContent key={key} value={key} className="space-y-4">
              {entries.length === 0 ? <EmptyState title="Nothing reported yet" /> : null}
              {entries.map((e) => (
                <Card key={`${e.device_id}-${e.kind}`}>
                  <CardHeader>
                    <CardTitle className="text-sm">
                      {deviceName(e.device_id)}{" "}
                      <span className="font-normal text-muted-foreground">· {timeAgo(e.hlc.wall_ms)}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="max-h-96 overflow-auto rounded-lg bg-muted/30 p-3 font-mono text-xs whitespace-pre-wrap">
                      {typeof e.payload === "string" ? e.payload : JSON.stringify(e.payload, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </>
  );
}
